<template>
  <div v-if="isLoading" class="flex items-center justify-center min-h-screen">
    <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>
  
  <template v-else>
    <button @click="toggleSidebar"
      class="fixed top-20 left-4 z-60 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 flex items-center md:flex"
      :class="{ 'left-[280px]': showSidebar }">
      <i class="pi pi-history"></i>
      <span class="text-white font-roboto ml-2 hidden md:inline">History</span>
    </button>
    
    
    <div
      class="fixed top-0 left-0 h-full w-full md:w-[380px] bg-white/30 transform transition-transform duration-300 ease-in-out z-50 pt-16"
      :class="{ '-translate-x-full': !showSidebar }"
      @click.stop>
      <button 
        @click="closeSidebar"
        class="absolute top-20 right-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200"
      >
        <i class="pi pi-times text-white"></i>
      </button>
      <div class="p-4">
        <InboxView class="overflow-y-auto h-full" />
      </div>
    </div>

    <div class="flex flex-col flex-shrink min-h-full p-2 md:p-6 transition-all duration-300"
      :class="{ 'ml-0 md:ml-[280px]': showSidebar }">
      <div
        class="box-border flex flex-col justify-center items-center mx-auto mt-4 md:mt-12 w-full h-auto max-w-[860px] px-2 md:px-6">
        <div
          class="flex flex-col items-center max-w-[860px] w-full bg-gradient-to-br from-slate-800 to-purple-400 rounded-lg shadow-2xl p-4 md:p-8 transition-all duration-500 ease-in-out"
          :class="{ 'scale-90 opacity-50': isSearchFocused || isFileInputFocused }">
          <div
            class="text-2xl md:text-4xl font-extrabold tracking-tight text-center text-white md:text-5xl sm:text-4xl sm:leading-tight transition-all duration-500 ease-in-out"
            :class="{ 'text-lg md:text-2xl': isSearchFocused || isFileInputFocused }">
            Welcome to <span class="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Paperboii</span>, {{ user_details.full_name }}
          </div>
          <div class="mt-4 md:mt-6 text-lg md:text-xl font-light text-center text-gray-200 transition-all duration-500 ease-in-out"
               :class="{ 'text-sm md:text-base mt-2 md:mt-3': isSearchFocused || isFileInputFocused }">
            Your AI-powered research companion
          </div>

          <!-- Usage Guide Section -->
          <div class="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full transition-all duration-500 ease-in-out"
               :class="{ 'hidden': isSearchFocused || isFileInputFocused }">
            <div class="bg-white/10 rounded-lg p-4 md:p-6 backdrop-blur-sm">
              <div class="text-white text-base md:text-lg font-semibold mb-2">📄 Upload PDF</div>
              <p class="text-gray-200 text-xs md:text-sm">
                Drop your research paper or click to upload. Support for papers up to 10MB.
              </p>
            </div>

            <div class="bg-white/10 rounded-lg p-4 md:p-6 backdrop-blur-sm">
              <div class="text-white text-base md:text-lg font-semibold mb-2">🔍 Search arXiv</div>
              <p class="text-gray-200 text-xs md:text-sm">
                Search and access papers directly from arXiv's extensive database.
              </p>
            </div>

            <div class="bg-white/10 rounded-lg p-4 md:p-6 backdrop-blur-sm">
              <div class="text-white text-base md:text-lg font-semibold mb-2">📚 Google Scholar</div>
              <p class="text-gray-200 text-xs md:text-sm">
                Find and analyze papers from Google Scholar's comprehensive collection.
              </p>
            </div>

            <div class="bg-white/10 rounded-lg p-4 md:p-6 backdrop-blur-sm">
              <div class="text-white text-base md:text-lg font-semibold mb-2">🔗 Use Direct URL</div>
              <p class="text-gray-200 text-xs md:text-sm">
                Paste a direct link to any accessible research paper PDF.
              </p>
            </div>
          </div>

          <div class="mt-6 text-center text-gray-200 text-sm transition-all duration-500 ease-in-out"
               :class="{ 'hidden': isSearchFocused || isFileInputFocused }">
            <p class="mb-2">
              Once your paper is loaded, chat with our AI to understand complex concepts,
              get summaries, or dive deep into specific sections.
            </p>
            <p class="text-xs text-gray-300">
              Choose your preferred method below to get started ↓
            </p>
          </div>
        </div>

        <section class="box-border flex flex-col w-full mt-6 md:mt-10 min-h-[100px]">
          <div class="flex flex-col w-full max-w-[860px] mx-auto space-y-4">
            <!-- Search Type Toggle -->
            <div v-if="useURL" class="flex justify-center mb-2">
              <div class="bg-white/10 p-1 rounded-lg inline-flex text-sm md:text-base">
                <button @click="setSearchType('arxiv')" class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  :class="searchType === 'arxiv' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-300 hover:text-white'">
                  arXiv Search
                </button>
                <button @click="setSearchType('scholar')" class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  :class="searchType === 'scholar' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-300 hover:text-white'">
                  Scholar Search
                </button>
                <button @click="setSearchType('url')" class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
                  :class="searchType === 'url' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-300 hover:text-white'">
                  Direct URL
                </button>
              </div>
              
            </div>

            <!-- Main Input Area -->
            <div class="flex flex-col gap-4">
              <!-- File Input / Search Input -->
              
              <div v-if="!useURL" 
                   @dragenter.prevent="dragActive = true"
                   @dragleave.prevent="dragActive = false"
                   @dragover.prevent
                   @drop.prevent="handleFileDrop"
                   @click="$refs.fileInput.click()"
                   class="w-full transition-all duration-300 cursor-pointer"
                   :class="{ 'scale-105': dragActive }">
                
                <div class="relative flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-lg transition-all duration-200"
                     :class="[
                       dragActive ? 'border-purple-400 bg-purple-50/10' : 'border-gray-300 bg-white/80',
                       isFileInputFocused ? 'scale-90' : ''
                     ]">
                  
                  <input
                    ref="fileInput"
                    type="file"
                    accept="application/pdf"
                    class="hidden"
                    @change="(e) => { pdfFile = e.target.files; handleFileUpload(); }"
                    @focus="isFileInputFocused = true"
                    @blur="handleFileInputBlur"
                  >
                  
                  <div class="flex flex-col items-center space-y-3">
                    <div class="p-3 rounded-full" 
                         :class="dragActive ? 'bg-purple-100/10' : 'bg-gray-50'">
                      <svg class="w-10 h-10" :class="dragActive ? 'text-purple-500' : 'text-gray-400'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                      </svg>
                    </div>
                    <div class="flex flex-col items-center space-y-1">
                      <span class="text-base font-medium" :class="dragActive ? 'text-purple-500' : 'text-gray-700'">
                        Drop your PDF here or click to browse
                      </span>
                      <span class="text-sm text-gray-500">
                        Support for papers up to 10MB
                      </span>
                    </div>
                  </div>
                  
                  <!-- Show selected file name if exists -->
                  <div v-if="pdfFile && pdfFile[0]" 
                       class="absolute bottom-2 left-2 right-2 mt-2 px-3 py-1 bg-gray-100 rounded-lg text-sm text-gray-700 truncate">
                    <div class="flex justify-between items-center">
                      <div class="truncate">{{ pdfFile[0].name }}</div>
                      <div class="text-xs text-gray-500">{{ (pdfFile[0].size / (1024 * 1024)).toFixed(2) }} MB</div>
                    </div>
                    
                    <!-- Upload progress bar for chunked uploads -->
                    <div v-if="isChunkedUpload && loading" class="upload-progress-bar mt-2">
                      <div class="upload-progress-fill" :style="{ width: `${uploadProgress}%` }"></div>
                    </div>
                    <div v-if="isChunkedUpload && !loading" class="text-xs text-gray-600 mt-1">
                      Large file detected: Will use chunked upload
                    </div>
                  </div>
                </div>
              </div>

              <div v-else class="relative">
                <div v-if="searchType !== 'url'" class="relative flex items-center">
                  <input v-model="searchQuery" 
                         @input="searchType === 'arxiv' ? searchArxiv() : searchScholar()"
                         @focus="isSearchFocused = true"
                         @blur="handleSearchBlur"
                         type="text"
                         :placeholder="searchType === 'arxiv' 
                           ? 'Search arXiv papers by title, author, or keywords...' 
                           : 'Search Google Scholar for papers...'"
                         class="w-full px-4 py-3 text-lg rounded-lg text-neutral-700 bg-white border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 transition duration-200 ease-in-out hover:scale-105" />
                  <div v-if="loadingSearch" class="absolute right-3 flex items-center">
                    <svg class="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                </div>
                <input v-else @input="handleInputChange" v-model="pdfUrl" type="url"
                  placeholder="Enter direct PDF URL (e.g., https://arxiv.org/pdf/2304.12210.pdf)"
                  class="w-full px-4 py-3 text-lg rounded-lg text-neutral-700 bg-white border border-gray-300 focus:border-blue-500 focus:ring focus:ring-blue-200 transition duration-200 ease-in-out hover:scale-105" />

                <!-- Update search results dropdown -->
                <div v-if="showSearchResults && searchResults.length > 0"
                  class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-y-auto max-h-60">
                  <div v-for="paper in searchResults" :key="paper.id || paper.url" 
                       @click="selectPaper(paper)"
                       class="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0">
                    <div class="font-medium">{{ paper.title }}</div>
                    <div class="text-sm text-gray-600">
                      {{ formatAuthors(paper.authors) }}
                      <span v-if="paper.year">({{ paper.year }})</span>
                      <span v-if="paper.citations" class="ml-2">Citations: {{ paper.citations }}</span>
                    </div>
                  </div>
                </div>
              </div>
              
            </div>

            <!-- Center align buttons on mobile -->
            <div class="flex flex-col md:flex-row gap-4 items-center justify-center md:justify-start">
              <!-- Upload/URL Toggle -->
              <div class="flex items-center justify-center bg-white/10 px-6 py-3 rounded-lg w-full md:w-auto">
                <div class="relative flex items-center space-x-6">
                  <!-- File Upload Option -->
                  <div class="flex items-center space-x-2 cursor-pointer" 
                       @click="useURL = false"
                       :class="!useURL ? 'text-white' : 'text-gray-400'">
                    <i class="pi pi-file-pdf text-lg"></i>
                    <span class="font-medium">File Upload</span>
                  </div>
                  
                  <!-- Toggle Switch -->
                  <div class="relative" @click="useURL = !useURL">
                    <div class="w-14 h-7 bg-gray-700/50 rounded-full p-1 cursor-pointer transition-colors duration-300"
                         :class="{ 'bg-purple-600/50': useURL }">
                      <div class="w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300"
                           :class="{ 'translate-x-7': useURL }">
                      </div>
                    </div>
                  </div>
                  
                  <!-- Search Option -->
                  <div class="flex items-center space-x-2 cursor-pointer"
                       @click="useURL = true"
                       :class="useURL ? 'text-white' : 'text-gray-400'">
                    <i class="pi pi-search text-lg"></i>
                    <span class="font-medium">Search</span>
                  </div>
                </div>
              </div>

              <!-- Start Chat Button -->
              <button @click.prevent="handleClick" :disabled="loading"
                class="whitespace-nowrap px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:from-blue-600 hover:to-purple-700 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto">
                {{ loading ? 'Processing...' : 'Start Chat' }}
              </button>
              
            </div>
          </div>

          <div v-if="error"
            class="mt-4 bg-red-100 border border-red-400 text-red-700 px-3 py-2 md:px-4 md:py-3 rounded relative text-sm md:text-base"
            role="alert">
            <strong class="font-bold">Error!</strong>
            <span class="error-message block sm:inline">
              Please enter a valid URL or upload a PDF file.
            </span>
            <span class="absolute top-0 bottom-0 right-0 px-4 py-3">
              <svg @click="error = false" class="fill-current h-6 w-6 text-red-500" role="button"
                xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <title>Close</title>
                <path
                  d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
              </svg>
            </span>
          </div>
          <div v-if="urlError" 
               class="mt-4 bg-amber-100 border border-amber-400 text-amber-700 px-3 py-2 md:px-4 md:py-3 rounded relative text-sm md:text-base"
               role="alert">
            <strong class="font-bold">Unable to fetch document!</strong>
            <span class="block sm:inline ml-1">
              There was an issue accessing the PDF. You can try downloading it directly and uploading the file:
            </span>
            <div class="mt-2">
              <a :href="pdfUrl" 
                 target="_blank"
                 class="inline-flex items-center px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors duration-200">
                <i class="pi pi-download mr-2"></i>
                Download PDF
              </a>
            </div>
            <span class="absolute top-0 bottom-0 right-0 px-4 py-3">
              <svg @click="urlError = false" 
                   class="fill-current h-6 w-6 text-amber-500 cursor-pointer" 
                   role="button"
                   xmlns="http://www.w3.org/2000/svg" 
                   viewBox="0 0 20 20">
                <title>Close</title>
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
              </svg>
            </span>
          </div>
          <!-- Loading indicators -->
          <div v-if="loading" 
               class="fixed inset-0 flex flex-col items-center justify-center bg-black/20 backdrop-blur-sm z-50">
            <div class="flex flex-col items-center">
              <div class="flex space-x-2">
                <div class="w-4 h-4 bg-purple-500 rounded-full animate-pulse"></div>
                <div class="w-4 h-4 bg-purple-500 rounded-full animate-pulse" style="animation-delay: 0.2s"></div>
                <div class="w-4 h-4 bg-purple-500 rounded-full animate-pulse" style="animation-delay: 0.4s"></div>
                <div class="w-4 h-4 bg-purple-500 rounded-full animate-pulse" style="animation-delay: 0.6s"></div>
              </div>
              <div class="text-gray-200 text-lg font-medium mt-4">
                {{ loadingMessage }}
              </div>
            </div>
          </div>
          <div v-if="showOCRDialog" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full"
            id="my-modal">
            <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div class="mt-3 text-center">
                <h3 class="text-lg leading-6 font-medium text-gray-900">OCR Required</h3>
                <div class="mt-2 px-7 py-3">
                  <p class="text-sm text-gray-500">
                    This PDF doesn't contain selectable text. Do you want to perform OCR to extract the text?
                  </p>
                </div>
                <div class="items-center px-4 py-3">
                  <button @click="cancelOCR"
                    class="px-4 py-2 bg-gray-200 text-gray-900 rounded-md mr-2 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300">
                    Cancel
                  </button>
                  <button @click="confirmOCR"
                    class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300">
                    Continue with OCR
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="ocrInProgress" 
               class="fixed inset-0 flex flex-col items-center justify-center bg-black/20 backdrop-blur-sm z-50">
            <div class="w-64 bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div class="bg-blue-600 h-2.5 rounded-full" 
                   :style="{ width: `${ocrProgress}%` }">
              </div>
            </div>
            <p class="mt-2 text-sm text-white">
              OCR Progress: {{ Math.round(ocrProgress) }}%
            </p>
          </div>
        </section>
      </div>
    </div>

    <!-- Add overlay when sidebar is open on mobile -->
    <div 
      v-if="showSidebar" 
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 cursor-pointer" 
      @click="closeSidebar">
    </div>
  </template>
</template>

<script>

import { mapMutations, mapGetters } from "vuex";
import { useCookies } from "vue3-cookies";
import { PDFDocument, rgb } from 'pdf-lib';
import { store } from '../store.js';
import InboxView from './InboxView.vue';
import axios from "axios";
import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import { debounce } from 'lodash';
import ChunkedUploader from '../utils/chunkedUploader.js';


pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.9.359/pdf.worker.min.js';

export default {
  name: 'HomePage',
  components: {
    InboxView
  },
  data() {
    return {
      pdfUrl: '',
      user: '',
      user_details: '',
      items: [],
      pdfFile: null,
      pdfData: null,
      loading: false,
      error: false,
      store,
      useURL: true,
      showOCRDialog: false,
      ocrInProgress: false,
      ocrProgress: 0,
      searchType: 'scholar',
      searchResults: [],
      showSearchResults: false,
      loadingSearch: false,
      loadingMessages: [
        'Processing your document...',
        'Analyzing the content...',
        'Almost there...',
        'Preparing your chat session...'
      ],
      loadingMessage: 'Processing your document...',
      loadingMessageInterval: null,
      maxFileSize: 100 * 1024 * 1024, // Increased to 100MB
      standardUploadLimit: 10 * 1024 * 1024, // 10MB limit for standard uploads
      showSidebar: false,
      loadingArxiv: false,
      isLoading: true,
      isSearchFocused: false,
      dragActive: false,
      isFileInputFocused: false,
      urlError: false,
      // New properties for chunked upload
      uploadProgress: 0,
      isChunkedUpload: false,
      // Job status polling properties
      jobId: null,
      statusPollInterval: null,
      pollAttempts: 0,
      maxPollAttempts: 120, // 120 attempts at 1 second intervals (2 minutes)
    }
  },
  methods: {
    handleInputChange(event) {
      this.error = false;
      this.loading = false;
    },
    userIsLoggedIn() {
      let user = this.$cookies.get('pb_user');
      this.user = user;
      return !(user == null || user == '' || user === undefined)
    },
    async handleFileUpload() {
      if (!this.pdfFile || !this.pdfFile[0]) {
        return;
      }

      const file = this.pdfFile[0];

      if (file.size > this.maxFileSize) {
        this.error = true;
        this.$nextTick(() => {
          document.querySelector('.error-message').textContent =
            `File size exceeds the maximum limit of ${this.maxFileSize / (1024 * 1024)}MB. Please choose a smaller file.`;
        });
        return;
      }

      this.isFileInputFocused = true;
      this.pdfUrl = file.name;
      
      // All files will use chunked upload - simply flag if it's a "large" file for UI purposes
      this.isChunkedUpload = file.size > this.standardUploadLimit;
    },

    async handleClick() {
      if (this.pdfFile === null && this.pdfUrl === '') {
        this.error = true;
        this.$nextTick(() => {
          document.querySelector('.error-message').textContent = 'The selected paper has an invalid URL' 
        });
        return;
      }

      let alteredPdfUrl = this.pdfUrl.replaceAll(':', '_');
      alteredPdfUrl = alteredPdfUrl.replaceAll('/', '_');
      this.loading = true;
      this.startLoadingAnimation();

      try {
        if (this.useURL) {
          try {
            const response = await axios.post(
              `${process.env.VUE_APP_BASE_URL}/start-chat?id=${alteredPdfUrl}`,
              { pdf_url: this.pdfUrl },
              {
                headers: {
                  'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                  'User': this.$cookies.get('pb_user'),
                  'Content-Type': 'application/json'
                }
              }
            );

            if (response.data) {
              if (response.status === 202) {
                // Async processing started, get the job ID and start polling
                this.jobId = response.data.job_id;
                this.pollAttempts = 0;
                this.pollJobStatus();
              } else {
                this.error = true;
                this.urlError = true;
                this.loading = false;
                this.stopLoadingAnimation();
              }
            }
          } catch (error) {
            this.urlError = true;
            this.error = false; // Hide the general error message
            throw new Error('URL access failed');
          }
        } else if (this.pdfFile && this.pdfFile[0]) {
          // Always use chunked upload, regardless of file size
          try {
            const file = this.pdfFile[0];
            const uploader = new ChunkedUploader({
              baseUrl: process.env.VUE_APP_BASE_URL,
              token: this.$cookies.get('pb_token'),
              user: this.$cookies.get('pb_user'),
              // For small files, use a single chunk
              chunkSize: (this.standardUploadLimit/2),
              onProgress: (progress) => {
                this.uploadProgress = progress;
                this.loadingMessage = file.size > this.standardUploadLimit ? 
                  `Uploading large file: ${Math.round(progress)}%` : 
                  `Processing file: ${Math.round(progress)}%`;
              }
            });
            
            const result = await uploader.uploadFile(file);
            
            if (result && result.doc_id) {
              const chatSession = await this.createChatSession(result.doc_id, result.file_id);
              this.$router.push(`/paper/${chatSession}`);
            } else if (result && result.job_id) {
              // If we got a job ID, start polling
              this.jobId = result.job_id;
              this.pollAttempts = 0;
              this.pollJobStatus();
            }
          } catch (error) {
            console.error('Upload error:', error);
            this.error = true;
            this.$nextTick(() => {
              document.querySelector('.error-message').textContent = 
                'Failed to upload file. Please try again.';
            });
            this.loading = false;
            this.stopLoadingAnimation();
          }
        } else {
          this.error = true;
          this.$nextTick(() => {
            document.querySelector('.error-message').textContent = 
              'No file selected or URL provided.';
          });
          this.loading = false;
          this.stopLoadingAnimation();
        }
      } catch (error) {
        console.error('Error:', error);
        if (!this.urlError) { // Only show the general error if urlError isn't shown
          this.error = true;
          if (error.response && error.response.status === 413) {
            this.$nextTick(() => {
              document.querySelector('.error-message').textContent =
                'API Gateway size limit exceeded. Use chunked upload for large files.';
            });
          }
        }
        this.stopLoadingAnimation();
        this.loading = false;
      }
    },

    // Helper method to create a chat session after successful chunked upload
    async createChatSession(doc_id, title) {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_URL}/create-chat-session`,
          { 
            doc_id: doc_id,
            title: title
          },
          {
            headers: {
              'Token': `Bearer ${this.$cookies.get('pb_token')}`,
              'User': this.$cookies.get('pb_user'),
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data && response.data.chat_id) {
          return response.data.chat_id;
        } else {
          throw new Error('No chat ID returned from server');
        }
      } catch (error) {
        console.error('Error creating chat session:', error);
        throw error;
      }
    },

    async confirmOCR() {
      this.showOCRDialog = false;
      this.ocrInProgress = true;
      
      try {
        // For OCR we need to:
        // 1. Get the file data (either from pdfData if already loaded or from the file)
        let pdfBase64Data;
        
        if (this.pdfData) {
          pdfBase64Data = this.pdfData;
        } else if (this.pdfFile && this.pdfFile[0]) {
          // Read the file data
          pdfBase64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const binaryData = e.target.result;
              const base64Data = btoa(
                new Uint8Array(binaryData).reduce(
                  (data, byte) => data + String.fromCharCode(byte),
                  ''
                )
              );
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(this.pdfFile[0]);
          });
        } else {
          throw new Error('No PDF data available for OCR');
        }
        
        // 2. Perform OCR on the PDF
        this.loadingMessage = 'Performing OCR on document...';
        const ocrResult = await this.performOCR(pdfBase64Data);
        
        // 3. Insert the OCR text into the PDF
        this.loadingMessage = 'Inserting OCR text into document...';
        const updatedPdfBase64 = await this.insertTextIntoPDF(pdfBase64Data, ocrResult);
        
        // 4. Store the OCR'd PDF data
        this.pdfData = updatedPdfBase64;
        
        // 5. Now upload the OCR'd PDF using chunked upload
        this.loadingMessage = 'Uploading OCR processed document...';
        const file = this.pdfFile[0];
        
        // Create a new File object with the OCR'd data
        const ocrFile = new File(
          [Uint8Array.from(atob(updatedPdfBase64), c => c.charCodeAt(0))], 
          file.name, 
          { type: 'application/pdf' }
        );
        
        // Use chunked uploader for the OCR'd file
        const uploader = new ChunkedUploader({
          baseUrl: process.env.VUE_APP_BASE_URL,
          token: this.$cookies.get('pb_token'),
          user: this.$cookies.get('pb_user'),
          chunkSize: ocrFile.size > this.standardUploadLimit ? 5 * 1024 * 1024 : ocrFile.size,
          onProgress: (progress) => {
            this.uploadProgress = progress;
            this.loadingMessage = `Uploading OCR processed document: ${Math.round(progress)}%`;
          }
        });
        
        const result = await uploader.uploadFile(ocrFile);
        
        if (result && result.doc_id) {
          const chatSession = await this.createChatSession(result.doc_id, result.file_id);
          this.$router.push(`/paper/${chatSession}`);
        } else if (result && result.job_id) {
          // If we got a job ID, start polling
          this.jobId = result.job_id;
          this.pollAttempts = 0;
          this.pollJobStatus();
        }
      } catch (error) {
        console.error('Error during OCR:', error);
        this.error = true;
        this.$nextTick(() => {
          document.querySelector('.error-message').textContent = 
            'Failed to process OCR. Please try again.';
        });
        this.loading = false;
        this.stopLoadingAnimation();
      } finally {
        this.ocrInProgress = false;
      }
    },

    cancelOCR() {
      this.showOCRDialog = false;
      this.loading = false;
      this.stopLoadingAnimation();
    },

    async processPDF(pdfBase64, alteredPdfUrl) {
      const pbUser = this.$cookies.get('pb_user');
      const pbToken = this.$cookies.get('pb_token');

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_URL}/start-chat?id=${alteredPdfUrl}`,
          pdfBase64,
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.status === 401) {
          window.location.href = '/about';
        }
        
        return response;
      } catch (error) {
        if (error.response && error.response.status === 413) {
          this.error = true;
          this.$nextTick(() => {
            document.querySelector('.error-message').textContent =
              'API Gateway size limit exceeded. Use chunked upload for large files.';
          });
        }
        throw error; // Re-throw the error to be handled by the calling function
      }
    },

    async extractTextFromPDF(pdfBase64) {
      const uint8Array = new Uint8Array(atob(pdfBase64).split('').map(char => char.charCodeAt(0)));
      const pdf = await pdfjsLib.getDocument({ data: uint8Array }).promise;
      let allText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        allText += pageText + '\n\n';
      }

      return allText;
    },

    async checkPDFHasText(pdfBase64) {
      const text = await this.extractTextFromPDF(pdfBase64);
      return text.trim().length > 0;
    },

    async getPDFData() {
      if (this.pdfData !== null) {
        return this.pdfData;
      }
      // Fetch the PDF data from the URL, and handle any errors
      return axios.get(this.pdfUrl, { responseType: 'arraybuffer' })
        .then((response) => {
          const base64Data = btoa(
            new Uint8Array(response.data).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              ''
            )
          );
          this.pdfData = base64Data;
          return base64Data;
        })
    },
    async performOCR(pdfBase64) {
      const worker = await createWorker('eng');
      await worker.loadLanguage('eng');
      await worker.initialize('eng');
      await worker.setParameters({
        tessedit_pageseg_mode: '1',
        tessedit_char_whitelist: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,!?@#$%^&*()_+-=[]{}|;:<>/',
      });

      const uint8Array = new Uint8Array(atob(pdfBase64).split('').map(char => char.charCodeAt(0)));
      const pdf = await pdfjsLib.getDocument({ data: uint8Array }).promise;
      console.log(`Number of pages: ${pdf.numPages}`);

      const processPage = async (pageNum) => {
        const page = await pdf.getPage(pageNum);
        const scale = 4; // Increased scale for better resolution
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        console.log(`Rendering page ${pageNum}`);

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
          enhanceTextSelection: true,
        };

        await page.render(renderContext).promise;

        // Apply image processing to improve OCR quality
        context.globalCompositeOperation = 'difference';
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.globalCompositeOperation = 'source-over';

        const imageData = canvas.toDataURL('image/png');
        const result = await worker.recognize(imageData);

        // Update progress
        this.ocrProgress = (pageNum / pdf.numPages) * 100;
        console.log(`Processed page ${pageNum} of ${pdf.numPages}`);

        return result.data.text;
      };

      const pagePromises = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        pagePromises.push(processPage(i));
      }

      const pageTexts = await Promise.all(pagePromises);
      const allText = pageTexts.join('\n\n');

      console.log("OCR completed. Text length:", allText.length);
      await worker.terminate();
      return allText;
    },

    async insertTextIntoPDF(pdfBase64, text) {
      const uint8Array = new Uint8Array(atob(pdfBase64).split('').map(char => char.charCodeAt(0)));
      const pdfDoc = await PDFDocument.load(uint8Array);
      const pages = pdfDoc.getPages();
      console.log(`Number of pages: ${pages.length}`);
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const { width, height } = page.getSize();
        page.drawText(text, {
          x: 10,
          y: height - 10,
          size: 0.5,
          color: rgb(0.0, 0.0, 0.0),
          opacity: 0.0,
        });
      }

      const pdfBytes = await pdfDoc.save();
      console.log(`PDF saved with ${pdfBytes} bytes`);
      return this.uint8ArrayToBase64(pdfBytes);
    },
    uint8ArrayToBase64(array) {
      let binary = '';
      const len = array.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(array[i]);
      }
      return btoa(binary);
    },
    setSearchType(type) {
      this.searchType = type;
      this.searchQuery = '';
      this.pdfUrl = '';
      this.searchResults = [];
      this.showSearchResults = false;
    },

    formatAuthors(authors) {
      if (!authors) return '';
      const authorList = Array.isArray(authors) ? authors : [authors];
      return authorList.slice(0, 3).join(', ') + (authorList.length > 3 ? '...' : '');
    },

    searchScholar: debounce(async function() {
      if (!this.searchQuery || this.searchQuery.length < 3) {
        this.searchResults = [];
        this.showSearchResults = false;
        return;
      }

      try {
        this.loadingSearch = true;
        const pbUser = this.$cookies.get('pb_user');
        const pbToken = this.$cookies.get('pb_token');
        
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_URL}/search-scholar?q=${encodeURIComponent(this.searchQuery)}`,
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser
            }
          }
        );

        if (response.data && response.data.results) {
          this.searchResults = response.data.results;
          this.showSearchResults = true;
        }
      } catch (error) {
        console.error('Error fetching Google Scholar results:', error);
        this.searchResults = [];
      } finally {
        this.loadingSearch = false;
      }
    }, 500),

    selectPaper(paper) {
      if (this.searchType === 'arxiv') {
        this.pdfUrl = paper.pdfLink.replace('http://', 'https://');
      } else {
        this.pdfUrl = paper.url;
      }
      this.searchQuery = paper.title;
      this.showSearchResults = false;
    },

    searchArxiv: debounce(async function() {
      if (!this.searchQuery || this.searchQuery.length < 3) {
        this.searchResults = [];
        this.showSearchResults = false;
        return;
      }

      try {
        this.loadingSearch = true;
        const response = await axios.get(`https://export.arxiv.org/api/query?search_query=all:${encodeURIComponent(this.searchQuery)}&start=0&max_results=10`);
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(response.data, "text/xml");
        const entries = xmlDoc.getElementsByTagName("entry");

        this.searchResults = Array.from(entries).map(entry => ({
          title: entry.getElementsByTagName("title")[0]?.textContent || '',
          authors: Array.from(entry.getElementsByTagName("author")).map(author =>
            author.getElementsByTagName("name")[0]?.textContent || ''
          ),
          id: entry.getElementsByTagName("id")[0]?.textContent || '',
          pdfLink: entry.getElementsByTagName("link")[1]?.getAttribute("href") || '',
        }));

        this.showSearchResults = true;
      } catch (error) {
        console.error('Error fetching arxiv results:', error);
        this.searchResults = [];
      } finally {
        this.loadingSearch = false;
      }
    }, 500),

    startLoadingAnimation() {
      let messageIndex = 0;
      this.loadingMessage = this.loadingMessages[0];
      this.loadingMessageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % this.loadingMessages.length;
        this.loadingMessage = this.loadingMessages[messageIndex];
      }, 3000);
    },

    stopLoadingAnimation() {
      if (this.loadingMessageInterval) {
        clearInterval(this.loadingMessageInterval);
        this.loadingMessageInterval = null;
      }
    },

    checkFileSize(size) {
      if (size > this.maxFileSize) {
        this.error = true;
        this.loading = false;
        return false;
      }
      return true;
    },

    toggleSidebar() {
      this.showSidebar = !this.showSidebar;
    },

    closeSidebar() {
      this.showSidebar = false;
    },

    async getUserDetails() {
      const pbUser = this.$cookies.get('pb_user');
      const pbToken = this.$cookies.get('pb_token');
      const response = await axios.get(
        `${process.env.VUE_APP_BASE_URL}/get-user-details`,
        {
          headers: {
            'Token': `Bearer ${pbToken}`,
            'User': pbUser
          }
        }
      );
      this.user_details = response.data;
    },

    handleSearchBlur() {
      // Only unfocus if there's no search query and no results showing
      if (!this.searchQuery && !this.showSearchResults) {
        this.isSearchFocused = false;
      }
    },

    handleFileDrop(e) {
      this.dragActive = false;
      const files = e.dataTransfer.files;
      if (files.length && files[0].type === 'application/pdf') {
        this.pdfFile = files;
        this.isFileInputFocused = true;
        this.handleFileUpload();
      }
    },

    handleFileInputBlur() {
      // Only unfocus if no file is selected
      if (!this.pdfFile || !this.pdfFile[0]) {
        this.isFileInputFocused = false;
      }
    },

    async pollJobStatus() {
      if (this.statusPollInterval) {
        clearInterval(this.statusPollInterval);
      }
      
      this.statusPollInterval = setInterval(async () => {
        // Check if we should stop polling
        if (!this.jobId || this.pollAttempts >= this.maxPollAttempts) {
          clearInterval(this.statusPollInterval);
          this.statusPollInterval = null;
          
          // If we reached max attempts, show an error
          if (this.pollAttempts >= this.maxPollAttempts) {
            this.error = true;
            this.loading = false;
            this.stopLoadingAnimation();
            this.$nextTick(() => {
              document.querySelector('.error-message').textContent = 
                'Document processing timed out. Please try again.';
            });
          }
          return;
        }
        
        this.pollAttempts++;
        
        try {
          const response = await axios.get(
            `${process.env.VUE_APP_BASE_URL}/get-job-status?job_id=${this.jobId}`,
            {
              headers: {
                'Token': `Bearer ${this.$cookies.get('pb_token')}`,
                'User': this.$cookies.get('pb_user')
              }
            }
          );
          
          console.log('Job status response:', response.data);
          
          if (response.data && response.data.status) {
            if (response.data.status === 'SUCCESS') {
              // Job completed successfully
              clearInterval(this.statusPollInterval);
              this.statusPollInterval = null;
              
              // Get the chat ID from the response and navigate to the paper chat page
              if (response.data.result && response.data.result.chat_id) {
                const chatId = response.data.result.chat_id;
                this.$router.push(`/paper/${chatId}`);
              } else {
                // If chat_id is not in the expected location, handle the error
                this.error = true;
                this.$nextTick(() => {
                  document.querySelector('.error-message').textContent = 
                    'Document processed but chat ID not found. Please try again.';
                });
              }
              
              this.loading = false;
              this.stopLoadingAnimation();
            } else if (response.data.status === 'FAILED') {
              // Job failed
              clearInterval(this.statusPollInterval);
              this.statusPollInterval = null;
              this.error = true;
              this.$nextTick(() => {
                document.querySelector('.error-message').textContent = 
                  response.data.error || 'Failed to process document. Please try again.';
              });
              
              this.loading = false;
              this.stopLoadingAnimation();
            }
            // If status is IN_PROGRESS, continue polling
          }
        } catch (error) {
          console.error('Error polling job status:', error);
          
          // If polling itself fails, don't stop trying unless we hit max attempts
          if (this.pollAttempts >= this.maxPollAttempts) {
            clearInterval(this.statusPollInterval);
            this.statusPollInterval = null;
            this.error = true;
            this.$nextTick(() => {
              document.querySelector('.error-message').textContent = 
                'Failed to get document processing status. Please try again.';
            });
            
            this.loading = false;
            this.stopLoadingAnimation();
          }
        }
      }, 1000); // Poll every 1 second
    },
  },
  computed: {
    paperRoute() {

      if (this.pdfUrl.length > 0) {

        return `/paper/${encodeURIComponent(this.pdfUrl)}`;
      }
      return '/';
    }
  },
  async created() {
    if (!this.userIsLoggedIn()) {
      this.$router.push('/about');
    } else {
      try {
        await this.getUserDetails();
      } finally {
        this.isLoading = false;
      }
    }
  },
  mounted() {
    if (!this.userIsLoggedIn()) {
      this.$router.push('/about');
    }
  },
  beforeUnmount() {
    this.stopLoadingAnimation();
    
    if (this.statusPollInterval) {
      clearInterval(this.statusPollInterval);
      this.statusPollInterval = null;
    }
  },
  watch: {
    useURL(newVal) {
      // Reset focus states when switching modes
      this.isSearchFocused = false;
      this.isFileInputFocused = false;
      
      // If switching to PDF mode, set file input focus
      if (!newVal) {
        this.isFileInputFocused = true;
      }
      // If switching to URL mode, set search focus
      else {
        this.isSearchFocused = true;
      }
    }
  },
};
</script>

<style scoped>
.container {
  max-width: 1024px;
}

h1 {
  color: #fff;
}

p {
  color: #ccc;
}

.bg-opacity-50 {
  --tw-bg-opacity: 0.5;
}

.shadow-lg {
  --tw-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}

.rounded-md {
  border-radius: 0.375rem;
}

.focus\:outline-none:focus {
  outline: 2px solid transparent;
  outline-offset: 2px;
}

.focus\:ring-2:focus {
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
}

.focus\:ring-gray-300:focus {
  --tw-ring-opacity: 1;
  --tw-ring-color: rgba(209, 213, 219, var(--tw-ring-opacity));
}

.focus\:ring-blue-300:focus {
  --tw-ring-opacity: 1;
  --tw-ring-color: rgba(147, 197, 253, var(--tw-ring-opacity));
}

.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

@media (max-width: 768px) {
  .v-switch {
    transform: scale(0.9);
  }

  input::placeholder {
    font-size: 14px;
  }

  .arxiv-results {
    max-height: 60vh;
    overflow-y: auto;
  }
}

.upload-progress-bar {
  width: 100%;
  height: 6px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 8px;
}

.upload-progress-fill {
  height: 100%;
  background-color: #8B5CF6;
  transition: width 0.3s ease;
}
</style>